import "chartjs-adapter-date-fns";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import React, { useImperativeHandle, useMemo } from "react";

// Register required components for Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChartCard = React.forwardRef(
  ({ card, topic, messages, w, h, displayVariant, displayParameters }, ref) => {
    // Action handler for interactivity (if needed)
    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    /**
     * Utility function to parse chart data.
     * Attempts to parse effectiveLastMessage.content as JSON.
     * If parsing fails, it falls back to extracting X and Y from safeMessages.
     */
    const parseChartData = (safeMessages, lastMessageContent) => {
      let chartData = {};
      try {
        chartData = JSON.parse(lastMessageContent);
        if (typeof chartData === "number") {
          throw new Error("Parsed JSON is a number, expected an object.");
        }
      } catch (e) {
        console.error("Invalid JSON in lastMessage.content:", e);
        // Fallback parsing logic
        chartData = { X: [], Y: [] };
        safeMessages.forEach((message) => {
          const timestamp = message.timestamp;
          const value = parseFloat(message.content.split(" ")[0]); // Ensure numeric
          if (!isNaN(value)) {
            chartData.X.push(new Date(timestamp)); // Convert to Date object
            chartData.Y.push(value);
          }
        });
      }
      return chartData;
    };

    // Create a safe messages array
    const safeMessages = messages && messages.length > 0 ? messages : [];
    // Derive effectiveLastMessage from safeMessages, with fallback content
    const effectiveLastMessage =
      safeMessages.length > 0
        ? safeMessages[safeMessages.length - 1]
        : { content: "{}", timestamp: null };

    // Memoize chart data to prevent unnecessary recalculations.
    const chartDataParsed = useMemo(
      () => parseChartData(safeMessages, effectiveLastMessage.content),
      [safeMessages, effectiveLastMessage.content]
    );

    // Extract keys and determine labels and dataset key.
    const keys = Object.keys(chartDataParsed);
    let labels = [];
    let datasetKey = "";
    if (keys.length >= 2) {
      labels = chartDataParsed[keys[0]] || [];
      datasetKey = keys[1];
    }

    // Validate data synchronization.
    const isDataValid =
      keys.length >= 2 &&
      Array.isArray(labels) &&
      Array.isArray(chartDataParsed[datasetKey]) &&
      labels.length === chartDataParsed[datasetKey].length;

    // Memoize data for Chart.js.
    const data = useMemo(
      () => ({
        labels,
        datasets: [
          {
            label: datasetKey || "Dataset",
            data: chartDataParsed[datasetKey] || [],
            backgroundColor: "rgba(75,192,192,0.4)",
            borderColor: "rgba(75,192,192,1)",
            borderWidth: 2,
            fill: false,
            tension: displayParameters.bezierCurve ? 0.4 : 0,
            pointRadius: 3,
            pointBackgroundColor: "rgba(75,192,192,1)",
            pointBorderColor: "#fff",
            pointHoverRadius: 5,
          },
        ],
      }),
      [labels, datasetKey, chartDataParsed, displayParameters]
    );

    // Memoize options for Chart.js based on displayParameters.
    const options = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: displayParameters.legendDisplay,
            position: "top",
          },
          title: {
            display: displayParameters.titleDisplay,
            text: topic.name,
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: "day",
              tooltipFormat: "yyyy-MM-dd HH:mm:ss",
              displayFormats: {
                day: "MM-dd",
                hour: "HH:mm",
              },
            },
            title: {
              display: displayParameters.xAxisTitleDisplay,
              text: "Timestamp",
            },
            grid: {
              display: true,
              color: "rgba(200,200,200,0.2)",
            },
          },
          y: {
            title: {
              display: displayParameters.yAxisTitleDisplay,
              text: "Value",
            },
            beginAtZero: displayParameters.yAxisBeginAtZero,
            grid: {
              display: true,
              color: "rgba(200,200,200,0.2)",
            },
            ticks: {
              color: "#666",
              font: {
                size: 12,
              },
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
        hover: {
          mode: "nearest",
          intersect: false,
        },
      }),
      [displayParameters, topic.name]
    );

    // Prepare render functions for different variants.
    const renderFunction = useMemo(
      () => ({
        line: <Line data={data} options={options} />,
        bar: <Bar data={data} options={options} />,
        pie: <Pie data={data} options={options} />,
      }),
      [data, options]
    );

    // Select the appropriate render variant.
    const renderVariant = renderFunction[displayVariant] || renderFunction.line; // Default to line chart.

    return (
      <div
        className="p-2 shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col"
        style={{
          backgroundColor: displayParameters.cardBackgroundColor || "#fafafa",
        }}
      >
        <div className="flex-grow relative h-full w-full">
          {isDataValid ? (
            renderVariant
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No data available to display.</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// Define variants and their parameters.
ChartCard.variants = ["default", "line", "bar", "pie"];
ChartCard.variantParameters = {
  line: {
    legendDisplay: {
      label: "Display Legend",
      type: "bool",
      default: true,
    },
    titleDisplay: {
      label: "Display Chart Title",
      type: "bool",
      default: false,
    },
    xAxisTitleDisplay: {
      label: "Display X-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisTitleDisplay: {
      label: "Display Y-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisBeginAtZero: {
      label: "Start Y-Axis at Zero",
      type: "bool",
      default: true,
    },
    bezierCurve: {
      label: "Enable Bezier Curve",
      type: "bool",
      default: false,
    },
  },
  bar: {
    legendDisplay: {
      label: "Display Legend",
      type: "bool",
      default: true,
    },
    titleDisplay: {
      label: "Display Chart Title",
      type: "bool",
      default: false,
    },
    xAxisTitleDisplay: {
      label: "Display X-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisTitleDisplay: {
      label: "Display Y-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisBeginAtZero: {
      label: "Start Y-Axis at Zero",
      type: "bool",
      default: true,
    },
  },
  pie: {
    legendDisplay: {
      label: "Display Legend",
      type: "bool",
      default: true,
    },
    titleDisplay: {
      label: "Display Chart Title",
      type: "bool",
      default: false,
    },
    xAxisTitleDisplay: {
      label: "Display X-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisTitleDisplay: {
      label: "Display Y-Axis Title",
      type: "bool",
      default: false,
    },
    yAxisBeginAtZero: {
      label: "Start Y-Axis at Zero",
      type: "bool",
      default: true,
    },
  },
};

export default ChartCard;
